import { QuestionnaireQuestions } from '@getpopsure/qnr-framework';
import LoadingSpinner from 'components/loadingSpinner';
import GenericReview from 'components/review';
import { generateCheckoutDocuments } from 'features/checkoutDocuments/actions';
import { Transform } from 'features/generalQuestionnaire/generalPolicy/actions';
import { GeneralQuestion } from 'features/generalQuestionnaire/model';
import { InsuranceTypes } from 'models/insurances/types';
import { Policy } from 'models/policies';
import { useState } from 'react';
import { useSafeTranslation } from 'shared/i18n';
import {
  QuestionReviewValueType,
  ReviewProps,
} from 'SignupQuestionnaire/components/Review/Review';

import { usePersistQnrAndQuote } from '../../hooks/usePersistQnrAndQuote';
import { useUpdatePolicy } from '../../hooks/useUpdatePolicy';
import {
  ADJUST_POLICY_QUESTION_ID,
  AdjustPolicyAnswers,
  AdjustPolicyDtoSchema,
} from '../../models';
import { getReviewValue } from './getReviewValues';
import { prepareReviewValues } from './prepareReviewValues';

const mergeTransforms = (t1?: Transform, t2?: Transform): Transform => {
  const { personalInfo: personalInfoT1 = [], tariffInfo: tariffInfoT1 = [] } =
    t1 ?? {};
  const { personalInfo: personalInfoT2 = [], tariffInfo: tariffInfoT2 = [] } =
    t2 ?? {};

  return {
    personalInfo: Array.from(new Set([...personalInfoT1, ...personalInfoT2])),
    tariffInfo: Array.from(new Set([...tariffInfoT1, ...tariffInfoT2])),
  };
};

export const AdjustPolicyReview = <
  Questionnaire extends QuestionnaireQuestions
>(
  props: Omit<
    ReviewProps<Questionnaire>,
    'createPostQuote' | 'insuranceType' | 'verticalId' | 'requestType'
  > & {
    meta: Record<string, unknown>;
    transform?: Transform;
    configuration: {
      questions: GeneralQuestion;
      policy: Policy;
    };
  }
) => {
  const {
    featureName,
    reviewValues,
    questionnaireAnswers,
    onSubmitValue,
    transform,
    meta = {},
    configuration,
    title,
    ctaText,
    description,
  } = props;
  const insuranceType = featureName.toUpperCase() as InsuranceTypes;
  const { t } = useSafeTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { adjustPolicy: { questionnaireTransform } = {} } =
    questionnaireAnswers as Partial<AdjustPolicyAnswers>;

  const { mutateAsync: persistQnrAndQuote } = usePersistQnrAndQuote();

  const { mutate: updatePolicy } = useUpdatePolicy();

  const { policy, questions } = configuration;

  const mappedReviewValues = reviewValues.flatMap((reviewItem) => {
    const questionId = reviewItem.path ?? '';

    const value = getReviewValue({
      questions,
      questionId,
      questionnaireAnswers,
    });

    return value !== null
      ? [
          {
            ...reviewItem,
            value,
          } as QuestionReviewValueType<Questionnaire>,
        ]
      : [];
  });

  const questionReviewValues = prepareReviewValues(
    questionnaireAnswers,
    mappedReviewValues
  );

  const hasAssociatedQuestion = (questionId: string) =>
    !!questions.find((q) => q.id === questionId);

  const questionReviewValuesWithFixedPaths = questionReviewValues.map((v) =>
    v.path && hasAssociatedQuestion(v.path)
      ? v
      : { ...v, path: ADJUST_POLICY_QUESTION_ID }
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const { quoteId, questionnaireId } = await persistQnrAndQuote({
      insuranceType: featureName.toUpperCase(),
      questionnaireAnswers,
      metas: meta,
      transform: mergeTransforms(
        transform,
        questionnaireTransform as Transform
      ),
    });

    await generateCheckoutDocuments(
      questionnaireId,
      quoteId as string,
      insuranceType,
      t
    );

    const adjustPolicyDto = await AdjustPolicyDtoSchema.parseAsync({
      ...questionnaireAnswers,
      questionnaireId,
      quoteId,
    });

    updatePolicy(
      {
        policyId: policy.id,
        adjustPolicyDto,
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onSubmitValue(true);
        },
      }
    );

    onSubmitValue(true);
  };

  if (isSubmitting) {
    return <LoadingSpinner />;
  }

  return (
    <GenericReview
      title={<h1 className="p-h1 mb8">{title}</h1>}
      handleSubmit={handleSubmit}
      insuranceType={featureName as InsuranceTypes}
      questionReviewValues={questionReviewValuesWithFixedPaths}
      ctaText={ctaText}
      confirmationText=""
      description={description}
      isDescriptionVisible
    />
  );
};
